import React, { memo, useContext, useEffect, useMemo } from 'react';

import cx from 'classnames';
import { filter, isEmpty, join, pipe } from 'ramda';

import Text from 'components/shared/Typography/Text';
import { LANDING_PAGE_ROLES } from 'constants/index';
import useElementSize from 'hooks/useElementSize';
import { transformBEScheduleToOverviewForm } from 'store/itineraryDetails/transform';
import { getDateFormat } from 'utils/date';

import { isDefined } from '../../../../../../../utils/ramda';
import { ListItem } from '../../../../Itinerary/Landing/Overview';
import { PDFDataContext } from '../../context';
import { useLayoutHeight, useRecursive } from '../../functions';
import SafariOverviewHeader from '../shared/SafariOverviewHeader';
import TemplatePage from '../TemplatePage';
import s from './index.module.css';

function SafariOverviewDetails({ onReady = () => {} }) {
  const { page } = useContext(PDFDataContext);
  const schedules = page?.schedules;
  const scheduleType = page?.scheduleType;
  const collection = useMemo(
    () =>
      splitDetailsByEnter(
        transformBEScheduleToOverviewForm(scheduleType, schedules, {
          includeOnlyDifferentYears: true,
          translate: true,
          translationDateFormat: getDateFormat(page?.settings?.displayDatesDdMmYyyy),
        }).filter(isListItem),
      ),
    [],
  );
  const { innerPages, handleReady, handleFail } = useRecursive({ items: collection, onReady });
  const pageJSON = page.landing.pages.find((p) => p.role === LANDING_PAGE_ROLES.SAFARI_OVERVIEW);
  const showCondition = !pageJSON || pageJSON.hidden || isEmpty(collection);

  useEffect(() => {
    if (showCondition) {
      onReady();
    }
  }, [collection]);

  if (showCondition) {
    return null;
  }

  const title = pageJSON?.title;
  const subTitle = pageJSON?.blocks?.[0]?.data?.subheadline || 'Detailed Itinerary';

  return (
    <>
      {innerPages.map((p, i) => (
        <MemoizedRecursiveSafariOverviewDetails
          key={i}
          title={title}
          subTitle={subTitle}
          blocks={p}
          onFail={handleFail}
          onReady={handleReady}
          isLastPage={i === innerPages.length - 1}
        />
      ))}
    </>
  );
}

export default SafariOverviewDetails;

function BlocksTarget({ blocks, isLastPage, onReady, onFail, size }) {
  const spaceRef = useLayoutHeight({ maxHeight: size.height, ignore: !isLastPage, blocks, onReady, onFail });

  return (
    <div ref={spaceRef} style={{ paddingTop: '20px' }}>
      <Text className={s.wrapper}>
        {blocks.map((item, i) => (
          <ListItem key={i} item={item} itemClass={cx(s.item, item.mbNone && s.mbNone)} />
        ))}
      </Text>
    </div>
  );
}

function RecursiveSafariOverviewDetails(props) {
  const { title, subTitle } = props;

  const [containerRef, containerSize] = useElementSize();

  return (
    <TemplatePage>
      <SafariOverviewHeader title={title} subTitle={subTitle} />
      <div ref={containerRef} style={{ height: '765px' }}>
        <BlocksTarget size={containerSize} {...props} />
      </div>
    </TemplatePage>
  );
}

function isListItem(item) {
  return item?.formattedRange || item?.detailsText || item?.accommodationText || item?.formattedNumberOfNights;
}

function splitDetailsByEnter(details) {
  return details.reduce((acc, cur) => {
    const detail = { ...cur };
    const summaryText = pipe(filter(isDefined), join('\n'))([detail.detailsText, detail.accommodationText]);

    detail.detailsText = summaryText;
    delete detail.accommodationText;

    const splitDetails = summaryText.split('\n');
    const subDetails = splitDetails.map((el, i) => {
      const det = { ...detail };
      det.detailsText = el || '\n';

      if (i < splitDetails.length - 1) {
        det.mbNone = true;
        delete det.formattedNumberOfNights;
      }

      if (i > 0) {
        delete det.formattedRange;
      }

      return det;
    });

    acc.push(...subDetails);

    return acc;
  }, []);
}

const MemoizedRecursiveSafariOverviewDetails = memo(
  RecursiveSafariOverviewDetails,
  (prev, next) => prev.blocks.length === next.blocks.length,
);
